/* Core & libraries */
import React, { useEffect } from "react";
import {Navigate, Route, Routes, useLocation, useSearchParams} from "react-router-dom";
import {defineMessages, useIntl} from "react-intl";
import {useTheme} from "react-jss";
import {storageFactory} from "factory/storageFactory";

/* Helpers, constants, custom methods, internal libraries */
import {
  ADMIN_COMPANIES_REMOVED_TAB_ROUTE,
  ADMIN_COMPANIES_ROUTE,
  ADMIN_COMPANY_DETAILS_ROUTE,
  ADMIN_SETTINGS_MAINTENANCE_ROUTE,
  ADMIN_SETTINGS_ROUTE,
  APP_PATH,
  ERROR_PAGE_ROUTE,
  ESCALATION_FORM_ROUTE,
  ESCALATION_MESSAGE_ROUTE,
  HOME_ROUTE,
  LEAVE_COMPANY,
  LOGIN_PATH,
  NOT_FOUND_ROUTE,
  PAYMENT_ROUTE,
  LOYALTY_ROUTE,
  TRAVEL_CREDITS_ROUTE,
  AVAILABLE_CREDITS_ROUTE,
  USED_CREDITS_ROUTE,
  EXPIRED_CREDITS_ROUTE,
  PEOPLE_ROUTE,
  PEOPLE_REMOVED_ROUTE,
  PEOPLE_TEAMS_ROUTE,
  POLICY_ROUTE,
  PROFILE_ROUTE,
  PROGRAM_ROUTE,
  REPORTS_BOOKING_ROUTE,
  REPORTS_MILEAGE_ROUTE,
  REPORTS_RECEIPTS_ROUTE,
  REPORTS_ROUTE,
  REVOKED_DOMAIN_ROUTE,
  ROOT_PATH,
  SAFETY_ROUTE,
  SETTINGS_EMAILDOMAINS_ROUTE,
  SETTINGS_INTEGRATIONS_ROUTE,
  SETTINGS_NOTIFICATIONS_ROUTE,
  SETTINGS_BOOKINGFIELDS_ROUTE,
  AIRLINEPROGRAMS_ROUTE,
  AIRLINEPROGRAMS_DELTAAIRLINES_ROUTE,
  AIRLINEPROGRAMS_DELTAAIRLINESPROGRAM_ROUTE,
  AIRLINEPROGRAMS_AMERICANAIRLINES_ROUTE,
  AIRLINEPROGRAMS_ALASKAAIRLINES_ROUTE,
  AIRLINEPROGRAMS_UNITEDAIRLINES_ROUTE,
  SETTINGS_ROUTE,
  SUCCESS_DOMAIN_ROUTE,
  TEAM_DETAILS_BOOKINGS_ROUTE,
  TEAM_DETAILS_ROUTE,
  TRAVELER_DETAILS_ROUTE,
  UNAUTHORIZED_PAGE_ROUTE,
  MAINTENANCE,
  COMPANY_DELETED_ROUTE,
  TRAVELER_DETAILS_JOIN_ROUTE,
  ADMIN_LOYALTY_ALLOTMENTS_ROUTE,
  ADMIN_LOYALTY_NOTIFICATIONS_ROUTE,
  ADMIN_LOYALTY_ROUTE,
  ADMIN_LOGIN_ROUTE,
  FAILED_LOYALTY_ROUTE,
  COMPLETED_LOYALTY_ROUTE,
  PENDING_LOYALTY_ROUTE,
  TEAM_DETAILS_SETTINGS_ROUTE,
  EDIT_POLICY_ROUTE,
  ADD_SPECIFIC_POLICY_ROUTE,
  EDIT_SPECIFIC_POLICY_ROUTE,
  ADMIN_PEOPLE_ROUTE,
  ADMIN_PEOPLE_REMOVED_TAB_ROUTE,
  ADMIN_TRAVELER_DETAILS_ROUTE,
  SETTINGS_FAREACCESS_ROUTE,
  DISCOUNTS_ROUTE,
  PEOPLE_GUEST_ROUTE,
  CREATE_GUEST_PROFILE,
  GUEST_DETAILS_ROUTE,
  ADMIN_LOYALTY_REQUESTS_ROUTE,
  FAILED_REQUESTS_LOYALTY_ROUTE,
  COMPLETED_REQUESTS_LOYALTY_ROUTE,
  PENDING_REQUESTS_LOYALTY_ROUTE,
  AADVANTAGE_PASS_ROUTE,
  REPORTS_HOTEL_BOOKING_ROUTE,
  REPORTS_SPEND_ROUTE,
  REPORTS_COMPLIANCE_ROUTE,
  REPORTS_BOOKING_SUMMARY,
  ADMIN_COMPANIES_PENDING_TAB_ROUTE,
  REPORTS_FLIGHT_SEGMENTS_ROUTE,
  PROMOTIONS_ROUTE,
  PROMOTIONS_PARTNER_ROUTE,
  REPORTS_TICKETS_ROUTE,
  REPORTS_COUPONS_ROUTE
} from "../../constants/routes";
import { isAdmin, saveCurrentLocation } from "../../helpers/general";
import { useUserContext } from "../../contexts/userContext";
import { useHasPermission } from "../../hooks/useHasPermission";
import { AIRLINES, DEFAULT_REDIRECT_ASSOCIATIONS, REDIRECT_ASSOCIATIONS } from "../../constants/appConstants";
import generalStyles from "../../resources/styles/general";
import { titleTranslations } from "../../constants/appConstants";
import { getLazyComponentWithSuspense, lazyWithPreload } from 'helpers/lazyLoadingHelpers';

/* Components */
import Login from "../../pages/login";
import Layout from "../layout";
import Home from "../../pages/home";
import Profile from "../../pages/profile";
import People from "../../pages/people";
import Program from "../../pages/program";
import AirlineProgramsContent from "../../pages/join";
import ProgramDL from "../../pages/programDL";
import DeltaJoinProgramPage from "../../pages/deltaJoinProgramPage";
import ProgramAA from "../../pages/programAA";
import ProgramAS from "../../pages/programAS";
import ProgramUA from "../../pages/programUA";
import Safety from "../../pages/safety";
import Policy from "../../pages/policy";
import Payment from "../../pages/payment";
import Loyalty from "../../pages/loyalty";
import Reports from "../../pages/reports";
import Settings from "../../pages/settings";
import TeamDetails from "../../pages/teamDetails";
import TravelerDetails from "../../pages/travelerDetails";
import EscalationForm from "../../pages/escalationPage";
import EscalationMessageSent from "../../pages/escalationMessageSent";
import LeaveCompany from "../../pages/leaveCompany";
import DomainRevoked from "../../pages/domainRevoked";
import DomainVerified from "../../pages/domainVerified";
import NotFound from "../../pages/notFound";
import Maintenance from "../../pages/maintenance";
import ErrorPage from "../../pages/errorPage";
import UnauthorizedPage from "../../pages/unauthorizedPage";
import AlaskaAdminLogin from "../../pages/alaskaAdminLogin";
import CompanyDeleted from "../../pages/companyDeleted";
import EditPolicy from "../../pages/editPolicy";
import EditHotelPolicy from "../../pages/editHotelPolicy";
import SpecificPolicy from "../../pages/editPolicy/specificPolicy";
import Discounts from "../../pages/discounts";
import GuestProfile from "../../pages/guestProfile";
import AAdvantagePass from "../../pages/AAdvantagePassPage";
import Promotions from "../../pages/promotions";


/* Styles */
import "../../resources/styles/flatpickr.css";

/* Components lazy-loaded */
// Note: pages lazy-loaded to reduce the main bundle/initial load size
export const AdminCompaniesLazy = lazyWithPreload(() => import('../../pages/admin/companies'));
export const CompanyDetailsLazy = lazyWithPreload(() => import('../../pages/admin/companyDetails'));
export const AdminSettingsLazy = lazyWithPreload(() => import('../../pages/admin/settings'));
export const AdminPeopleLazy = lazyWithPreload(() => import('../../pages/admin/people'));
export const AdminPeopleDetailsLazy = lazyWithPreload(() => import('../../pages/admin/peopleDetails'));
export const AdminLoyaltyLazy = lazyWithPreload(() => import('../../pages/admin/loyalty'));

const localStore = storageFactory(() => localStorage);

const AppRoutes = () => {
  const location = useLocation();

  const [qsp] = useSearchParams();
  const userContext = useUserContext();
  const intl = useIntl();
  const { checkPermission } = useHasPermission();
  const theme = useTheme();

  const general = generalStyles({ theme });

  const getRedirectUrl = () => {
    if(userContext?.loggedIn) {
      const prev = localStore.getItem("prevLocations");
      const prevLocations = prev ? JSON.parse(prev) : null;

      if(userContext?.userInfo.providerInfo?.providerCode === AIRLINES.AA &&
        prevLocations &&
        location.pathname.replace(/\//g, "") === APP_PATH.replace(/\//g, "") &&
        prevLocations.includes(ESCALATION_FORM_ROUTE))
      {
        //clear storage of prevLocations
        localStore.removeItem("prevLocations");
        return ESCALATION_FORM_ROUTE;
      }
      const origin = qsp.get("from") ? "?from=" + qsp.get("from") : "";

      return userContext?.userInfo?.landingPage && !userContext?.manageCompany
        ? REDIRECT_ASSOCIATIONS[userContext?.userInfo?.landingPage] + origin
        : (DEFAULT_REDIRECT_ASSOCIATIONS[userContext?.userInfo.providerInfo?.providerCode] ?? HOME_ROUTE);
    }
    return LOGIN_PATH
  }

  const getAirlineTitle = () => {
    const messages = defineMessages(titleTranslations[userContext?.userInfo.providerInfo?.providerCode])
    if (isAdmin(userContext)) {
      document.title = intl.formatMessage(messages.admin)
    } else {
      document.title = intl.formatMessage(messages.title)
    }
  }

  React.useEffect(() => {
    getAirlineTitle();
  }, [])

  useEffect(() => {
    //save in context only if not logged in to achieve deeplinking
    !userContext.loggedIn && saveCurrentLocation(location.pathname);
  }, [location.pathname])

  const hasPermission = (permission, isAdminRoute = false) => {
    const adminUser = isAdmin(userContext);
    const hasAccess = isAdminRoute ? adminUser : !adminUser;

    return userContext?.loggedIn && checkPermission(permission) && hasAccess
  }

  const renderRouteByPermission = (route, element, permission, isAdminRoute = false) => {
    return <Route path={ route } element={ hasPermission(permission, isAdminRoute) ? element : <Navigate to={LOGIN_PATH} replace /> }/>
  }

  const renderRouteIfLoggedOut = (route, element) => {
    return <Route path={ route } element={ !userContext?.loggedIn ? element : <Navigate to={ getRedirectUrl() } replace /> }/>
  }

  const renderRouteIfLoggedIn = (route, element, permission) => {
    return <Route path={ route } element={ userContext?.loggedIn && hasPermission(permission) ? element : <Navigate to={LOGIN_PATH} replace /> }/>
  }

  const getQsp = () => {

  }

  return (
    <div className={general.formFields}>
      <Routes>
        <Route
          path={ APP_PATH }
          element={
            <Navigate state={qsp.get("from")} to={ getRedirectUrl() } replace />
          }
        />
        <Route
          path={ ROOT_PATH }
          element={
            <Navigate to={ getRedirectUrl() } replace />
          }
        />
        { renderRouteIfLoggedOut(LOGIN_PATH, <div className={general.main}><Login /></div>) }

        <Route element={ <Layout /> }>
          { renderRouteByPermission(HOME_ROUTE, <Home />, "F_VIEW_COMPANYPROGRAM") }
          { renderRouteByPermission(PROFILE_ROUTE, <Profile />, "F_VIEW_MYPROFILE") }
          { renderRouteByPermission(PEOPLE_ROUTE, <People />, "F_VIEW_USERS") }
          { renderRouteByPermission(PEOPLE_GUEST_ROUTE, <People />, "F_GUEST_PROFILES_VIEW") }
          { renderRouteByPermission(CREATE_GUEST_PROFILE, <GuestProfile />, "F_GUEST_PROFILE_MANAGE") }
          { renderRouteByPermission(GUEST_DETAILS_ROUTE, <GuestProfile />, "F_GUEST_PROFILE_VIEW") }
          { renderRouteByPermission(PROGRAM_ROUTE, <Program />, "F_VIEW_USERS") }
          { renderRouteByPermission(SAFETY_ROUTE, <Safety />, "F_VIEW_TRIPBOARD") }
          { renderRouteByPermission(POLICY_ROUTE, <Policy />, "F_VIEW_POLICIES") }
          { renderRouteByPermission(PAYMENT_ROUTE, <Payment />, "F_VIEW_PAYMENTS") }
          { renderRouteByPermission(LOYALTY_ROUTE, <Loyalty />, "F_LEP_COMPANY_VIEW") }
          { renderRouteByPermission(TRAVEL_CREDITS_ROUTE, <Payment />, "F_VIEW_TRIPCREDITS") }
          { renderRouteByPermission(AVAILABLE_CREDITS_ROUTE, <Payment />, "F_VIEW_TRIPCREDITS") }
          { renderRouteByPermission(USED_CREDITS_ROUTE, <Payment />, "F_VIEW_TRIPCREDITS") }
          { renderRouteByPermission(EXPIRED_CREDITS_ROUTE, <Payment />, "F_VIEW_TRIPCREDITS") }
          { renderRouteByPermission(REPORTS_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_BOOKING_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_MILEAGE_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_RECEIPTS_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_BOOKING_SUMMARY, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_HOTEL_BOOKING_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_SPEND_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_COMPLIANCE_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_FLIGHT_SEGMENTS_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_TICKETS_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(REPORTS_COUPONS_ROUTE, <Reports />, "F_VIEW_REPORTS") }
          { renderRouteByPermission(SETTINGS_ROUTE, <Settings />, "F_VIEW_COMPANYSETTINGS") }
          { renderRouteByPermission(SETTINGS_EMAILDOMAINS_ROUTE, <Settings />, "F_VIEW_COMPANYSETTINGS") }
          { renderRouteByPermission(SETTINGS_INTEGRATIONS_ROUTE, <Settings />, "F_VIEW_COMPANYSETTINGS") }
          { renderRouteByPermission(SETTINGS_NOTIFICATIONS_ROUTE, <Settings />, "F_VIEW_COMPANYSETTINGS") }
          { renderRouteByPermission(SETTINGS_BOOKINGFIELDS_ROUTE, <Settings />, "F_VIEW_COMPANYSETTINGS") }
          { renderRouteByPermission(PEOPLE_REMOVED_ROUTE, <People />, "F_VIEW_USERS") }
          { renderRouteByPermission(AIRLINEPROGRAMS_ROUTE, <AirlineProgramsContent />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }
          { renderRouteByPermission(AIRLINEPROGRAMS_DELTAAIRLINES_ROUTE, <ProgramDL />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }
          { renderRouteByPermission(SETTINGS_FAREACCESS_ROUTE, <Settings />, "F_VIEW_COMPANYSETTINGS") }

          { renderRouteByPermission(AIRLINEPROGRAMS_AMERICANAIRLINES_ROUTE, <ProgramAA />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }
          { renderRouteByPermission(AIRLINEPROGRAMS_ALASKAAIRLINES_ROUTE, <ProgramAS />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }
          { renderRouteByPermission(AIRLINEPROGRAMS_UNITEDAIRLINES_ROUTE, <ProgramUA />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }
          { renderRouteByPermission(PEOPLE_TEAMS_ROUTE, <People />, "F_VIEW_USERS") }
          { renderRouteByPermission(TEAM_DETAILS_ROUTE, <TeamDetails />, "F_VIEW_USERS") }
          { renderRouteByPermission(TEAM_DETAILS_BOOKINGS_ROUTE, <TeamDetails />, "F_VIEW_USERS") }
          { renderRouteByPermission(TEAM_DETAILS_SETTINGS_ROUTE, <TeamDetails />, "F_VIEW_USERS") }
          { renderRouteByPermission(TRAVELER_DETAILS_ROUTE, <TravelerDetails />, "F_VIEW_USERS") }
          { renderRouteByPermission(TRAVELER_DETAILS_JOIN_ROUTE, <TravelerDetails />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }
          { renderRouteByPermission(EDIT_POLICY_ROUTE, userContext?.userInfo.providerInfo?.providerCode === AIRLINES.CHOICE ? <EditHotelPolicy /> : <EditPolicy />, "F_VIEW_POLICIES") }
          { renderRouteByPermission(ADD_SPECIFIC_POLICY_ROUTE, <SpecificPolicy />, "F_VIEW_POLICIES") }
          { renderRouteByPermission(EDIT_SPECIFIC_POLICY_ROUTE, <SpecificPolicy />, "F_VIEW_POLICIES") }
          { renderRouteByPermission(DISCOUNTS_ROUTE, <Discounts />, "F_DISCOUNTTIER_VIEWTIERLEVEL") }
          { renderRouteByPermission(AADVANTAGE_PASS_ROUTE, <AAdvantagePass />, "F_AADVANTAGE_PASS_VIEW") }
          { renderRouteByPermission(PROMOTIONS_ROUTE, <Promotions />, "F_VIEW_PROMOTIONS") }
          { renderRouteByPermission(PROMOTIONS_PARTNER_ROUTE, <Promotions />, "F_VIEW_PROMOTIONS") }

          { /* admin routes */ }
          { renderRouteByPermission(ADMIN_COMPANIES_ROUTE, getLazyComponentWithSuspense(AdminCompaniesLazy), "F_VIEW_PROVIDER_COMPANIES", true) }   
          { renderRouteByPermission(ADMIN_COMPANIES_REMOVED_TAB_ROUTE, getLazyComponentWithSuspense(AdminCompaniesLazy), "F_VIEW_PROVIDER_COMPANIES", true) }
          { renderRouteByPermission(ADMIN_COMPANIES_PENDING_TAB_ROUTE, getLazyComponentWithSuspense(AdminCompaniesLazy), "F_VIEW_PROVIDER_COMPANIES", true) }
          { renderRouteByPermission(ADMIN_COMPANY_DETAILS_ROUTE, getLazyComponentWithSuspense(CompanyDetailsLazy), "F_VIEW_PROVIDER_COMPANIES", true) }
          { renderRouteByPermission(ADMIN_SETTINGS_ROUTE, getLazyComponentWithSuspense(AdminSettingsLazy), "F_PROVIDER_SETTINGS", true) }
          { renderRouteByPermission(ADMIN_SETTINGS_MAINTENANCE_ROUTE, getLazyComponentWithSuspense(AdminSettingsLazy), "F_PROVIDER_SETTINGS", true) }
          { renderRouteByPermission(ADMIN_PEOPLE_ROUTE, getLazyComponentWithSuspense(AdminPeopleLazy), "F_VIEW_PROVIDER_USERS", true) }
          { renderRouteByPermission(ADMIN_PEOPLE_REMOVED_TAB_ROUTE, getLazyComponentWithSuspense(AdminPeopleLazy), "F_VIEW_PROVIDER_USERS", true) }
          { renderRouteByPermission(ADMIN_TRAVELER_DETAILS_ROUTE, getLazyComponentWithSuspense(AdminPeopleDetailsLazy), "F_VIEW_PROVIDER_USERS", true) }


          {/* Loyalty program only for AA! Based on flag from /me */}
          { userContext?.userInfo?.showCompanyLoyalty && (
            <>
              { renderRouteByPermission(ADMIN_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEWLEPSETTINGS", true) }
              { renderRouteByPermission(ADMIN_LOYALTY_ALLOTMENTS_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEWALLOTMENTS", true) }
              { renderRouteByPermission(ADMIN_LOYALTY_NOTIFICATIONS_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEWALLOTMENTS", true) }
              { renderRouteByPermission(FAILED_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEWALLOTMENTS", true) }
              { renderRouteByPermission(COMPLETED_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEWALLOTMENTS", true) }
              { renderRouteByPermission(PENDING_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEWALLOTMENTS", true) }
              { renderRouteByPermission(ADMIN_LOYALTY_REQUESTS_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEW_REDEMPTIONSREQUESTS", true) }
              { renderRouteByPermission(FAILED_REQUESTS_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEW_REDEMPTIONSREQUESTS", true) }
              { renderRouteByPermission(COMPLETED_REQUESTS_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEW_REDEMPTIONSREQUESTS", true) }
              { renderRouteByPermission(PENDING_REQUESTS_LOYALTY_ROUTE, getLazyComponentWithSuspense(AdminLoyaltyLazy), "F_LOYALTYPOINTS_VIEW_REDEMPTIONSREQUESTS", true) }
            </>
          )}

        </Route>

        <Route path={ ADMIN_LOGIN_ROUTE } element={ <AlaskaAdminLogin /> } />

        { renderRouteByPermission(AIRLINEPROGRAMS_DELTAAIRLINESPROGRAM_ROUTE, <DeltaJoinProgramPage />, "F_VIEW_USERDASHBOARD_AVAILABLEPROVIDERS") }

        { renderRouteIfLoggedIn(ESCALATION_FORM_ROUTE, <EscalationForm />, "F_VIEW_ESCALATIONFORM") }
        { renderRouteIfLoggedIn(ESCALATION_MESSAGE_ROUTE, <EscalationMessageSent />, "F_VIEW_ESCALATIONFORM_CONFIRMATION") }

        <Route path={ COMPANY_DELETED_ROUTE } element={ <CompanyDeleted /> } />

        <Route path={ LEAVE_COMPANY } element={ <LeaveCompany /> } />
        <Route path={ REVOKED_DOMAIN_ROUTE } element={ <DomainRevoked /> } />
        <Route path={ SUCCESS_DOMAIN_ROUTE } element={ <DomainVerified /> } />
        <Route path={ NOT_FOUND_ROUTE } element={ <NotFound /> } />
        <Route path={ MAINTENANCE } element={ <Maintenance /> } />
        <Route path={ ERROR_PAGE_ROUTE } element={ <ErrorPage /> } />
        <Route path={ UNAUTHORIZED_PAGE_ROUTE } element={ <UnauthorizedPage /> } />

        <Route
          path="*"
          element={
            <Navigate to={ NOT_FOUND_ROUTE } replace />
          }
        />
      </Routes>
    </div>
  )
}

export default AppRoutes;
